import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { useUserStatusContext } from '../../../context/UserStatus';
import { UserList } from '../types';
import { USERS_LIST_KEY } from './constants';

type GetUsersListParams = {
    status?: string;
    role?: string;
    search?: string;
    sort_by?: string; // email | created
    sort_dir?: string; // ASC | DESC
    offset?: number;
    limit?: number;
};

type Options = { enabled?: boolean; allPages?: boolean; retry?: false };

const useGetUsersList = (
    query: GetUsersListParams,
    { enabled = true, allPages = false, retry }: Options = {}
) => {
    const req = useRequest();
    const { activeOrgId, userStatus } = useUserStatusContext();
    const orgId = activeOrgId ?? userStatus?.hero_organization_id;

    const request = useCallback(async () => {
        const first = await req<UserList>({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/external-users/`,
            query
        });
        if (!allPages) return first;
        const items = [...first.items];
        let offset = (query.offset ?? 0) + first.items.length;
        while (first.items.length > 0 && offset < (first.total_count ?? offset)) {
            const page = await req<UserList>({
                url: `${envVars.API_RTM_HOSTNAME}enterprise/external-users/`,
                query: { ...query, offset }
            });
            if (!page.items.length) break;
            items.push(...page.items);
            offset += page.items.length;
        }
        return { ...first, items, limit: items.length };
    }, [query, req, allPages]);

    return useQuery({
        queryFn: request,
        queryKey: [USERS_LIST_KEY, orgId, query, allPages],
        enabled: enabled && Boolean(orgId),
        ...(allPages && { staleTime: 5 * 60 * 1000 }),
        ...(retry !== undefined && { retry })
    });
};

export default useGetUsersList;
